/**
 * Rendimiento por plantilla: tabla ordenable (entrega, lectura, respuesta, bajas, enviados) con
 * el estado, los chips «Simulada» y «De otra cuenta» y las horas hasta la aprobación. Cada fila
 * lleva al detalle de la plantilla.
 */

import { Link } from "react-router-dom";
import type { FilaPlantilla, OrdenPlantillas } from "../../types.estadisticas";
import { NOTA_BASES_TASAS, ordenarPlantillas, tasaTexto } from "../../logic/estadisticas";
import { usd } from "../../logic/formato";
import { CATEGORIA } from "../../logic/textos";
import { ChipEstadoPlantilla } from "../ChipEstadoPlantilla";
import { num } from "../../../campana-nueva/utils/format";
import { Vacio } from "./comun";

const COLUMNAS: { orden: OrdenPlantillas; label: string }[] = [
  { orden: "enviados", label: "Enviados" },
  { orden: "entrega", label: "Entrega" },
  { orden: "lectura", label: "Lectura" },
  { orden: "respuesta", label: "Respuesta" },
  { orden: "bajas", label: "Bajas" },
];

export function TablaPlantillas({ filas, orden, onOrden }: { filas: FilaPlantilla[] | null | undefined; orden: OrdenPlantillas; onOrden: (o: OrdenPlantillas) => void }) {
  const lista = ordenarPlantillas(filas, orden);
  if (!lista.length) return <Vacio>Todavía no hay plantillas enviadas a Meta.</Vacio>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        {/* las tasas no van todas sobre lo mismo: se dice (antes no, y no cerraban con el embudo) */}
        <caption className="caption-bottom pt-2 text-left text-[11px] text-slate-400">{NOTA_BASES_TASAS}</caption>
        <thead>
          <tr className="text-left text-[11px] font-bold uppercase tracking-wider text-slate-400">
            <th scope="col" className="py-2 pr-3">Plantilla</th>
            {COLUMNAS.map((c) => (
              <th key={c.orden} scope="col" className="py-2 pr-3 text-right" aria-sort={orden === c.orden ? "descending" : "none"}>
                <button type="button" onClick={() => onOrden(c.orden)}
                  className={`uppercase tracking-wider hover:text-slate-700 ${orden === c.orden ? "text-slate-900" : ""}`}>
                  {c.label}{orden === c.orden ? " ↓" : ""}
                </button>
              </th>
            ))}
            <th scope="col" className="py-2 pr-3 text-right">Gasto</th>
            <th scope="col" className="py-2 text-right">Aprobación</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {lista.map((f) => (
            <tr key={f.id} className="align-top hover:bg-slate-50/60">
              <td className="py-2.5 pr-3 min-w-[200px]">
                <Link to={`/activa/oficial/plantillas/${encodeURIComponent(f.id)}`} className="font-medium text-slate-900 hover:text-violet-700">{f.nombreVisible}</Link>
                <div className="mt-1 flex items-center gap-1.5 flex-wrap">
                  <ChipEstadoPlantilla estado={f.estado} />
                  <span className="text-[10px] text-slate-400">{CATEGORIA[f.categoria as keyof typeof CATEGORIA] ?? f.categoria}{f.recategorizada ? " (recategorizada)" : ""}</span>
                  {f.simulada && <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-50 text-amber-700">Simulada</span>}
                  {f.deOtraCuenta && <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-red-50 text-red-600">De otra cuenta</span>}
                </div>
              </td>
              <td className="py-2.5 pr-3 text-right tabular-nums text-slate-900">{num(f.enviados)}</td>
              <td className="py-2.5 pr-3 text-right tabular-nums text-slate-700">{f.enviados ? tasaTexto(f.tasas?.entrega) : "—"}</td>
              <td className="py-2.5 pr-3 text-right tabular-nums text-slate-700">{f.enviados ? tasaTexto(f.tasas?.lectura) : "—"}</td>
              <td className="py-2.5 pr-3 text-right tabular-nums text-slate-700">{f.enviados ? tasaTexto(f.tasas?.respuesta) : "—"}</td>
              <td className="py-2.5 pr-3 text-right tabular-nums text-slate-700">{f.enviados ? tasaTexto(f.tasas?.bajas) : "—"}</td>
              <td className="py-2.5 pr-3 text-right tabular-nums text-slate-700">{usd(f.cobradoClienteUSD)}</td>
              <td className="py-2.5 text-right text-xs text-slate-500 whitespace-nowrap">
                {f.horasHastaAprobacion !== null && Number.isFinite(Number(f.horasHastaAprobacion)) ? `${num(Math.round(Number(f.horasHastaAprobacion) * 10) / 10)} h` : "—"}
                {f.rechazos > 0 && <span className="block text-red-600">{num(f.rechazos)} {f.rechazos === 1 ? "rechazo" : "rechazos"}</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default TablaPlantillas;
